import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, Building2, Loader2, X, Edit3 } from "lucide-react";

// V1.25.3 · CRUD de Centros de Reconocimiento Médico DGT.
// El número oficial (`recognition_center_number`, ej. "M-1234") se hereda
// automáticamente a los documentos DGT del alumno seleccionado — sin
// duplicación en la ficha del alumno.

function CenterForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    recognition_center_number: initial?.recognition_center_number || "",
    address: initial?.address || "",
    phone: initial?.phone || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.patch(`/recognition-centers/${initial.id}`, form);
        toast.success("Centro actualizado.");
      } else {
        await api.post("/recognition-centers", form);
        toast.success("Centro creado.");
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border-2 border-orange-300 bg-orange-50/40 p-4 space-y-3" data-testid="recognition-center-form">
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
            Nombre del centro *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            placeholder="ej: Centro Médico Móstoles"
            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
            data-testid="rc-name-input"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
            Número Centro DGT
          </label>
          <input
            type="text"
            value={form.recognition_center_number}
            onChange={(e) => setForm({ ...form, recognition_center_number: e.target.value })}
            placeholder="ej: M-1234"
            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm font-mono"
            data-testid="rc-number-input"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
            Dirección
          </label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
            data-testid="rc-address-input"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
            Teléfono
          </label>
          <input
            type="text"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
            data-testid="rc-phone-input"
          />
        </div>
      </div>
      <div>
        <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
          Notas internas
        </label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
          data-testid="rc-notes-input"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1 rounded-md bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          data-testid="rc-save-btn"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {initial?.id ? "Guardar cambios" : "Crear centro"}
        </button>
      </div>
    </form>
  );
}

export default function RecognitionCentersManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // center object being edited
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recognition-centers");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (center) => {
    if (!window.confirm(`¿Eliminar el centro "${center.name}"? Los documentos ya generados no se ven afectados (snapshot inmutable).`)) return;
    try {
      await api.delete(`/recognition-centers/${center.id}`);
      toast.success("Centro eliminado.");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 md:p-6 space-y-3" data-testid="recognition-centers-manager">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-orange-500" />
          <h2 className="font-bold text-base">Centros de Reconocimiento Médico</h2>
          <span className="text-[10px] font-mono rounded bg-orange-100 text-orange-800 px-1.5 py-0.5">V1.25.3</span>
        </div>
        {!creating && !editing && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 text-xs font-semibold"
            data-testid="rc-create-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Nuevo centro
          </button>
        )}
      </div>

      <p className="text-xs text-neutral-500">
        El <strong>Número Centro DGT</strong> (ej. <code className="font-mono bg-neutral-100 px-1 rounded">M-1234</code>) se rellena
        automáticamente en los formularios DGT de cada alumno que tenga este centro asignado. No se duplica en la ficha del alumno.
      </p>

      {creating && (
        <CenterForm
          initial={null}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <CenterForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-neutral-300 py-6 text-center text-sm text-neutral-500">
          Sin centros registrados. Añade el primero.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col />
              <col style={{ width: "140px" }} />
              <col style={{ width: "260px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
            </colgroup>
            <thead>
              <tr className="border-b border-neutral-200 text-[11px] uppercase text-neutral-500">
                <th className="text-left py-3 px-4">Nombre</th>
                <th className="text-left py-3 px-4">Nº DGT</th>
                <th className="text-left py-3 px-4 hidden md:table-cell">Dirección</th>
                <th className="text-left py-3 px-4 hidden md:table-cell">Teléfono</th>
                <th className="text-right py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-neutral-100 hover:bg-neutral-50 h-[56px]" data-testid={`rc-row-${c.id}`}>
                  <td className="py-3 px-4 font-semibold align-middle truncate" title={c.name}>{c.name}</td>
                  <td className="py-3 px-4 font-mono align-middle truncate">
                    {c.recognition_center_number || (
                      <span className="text-amber-600 text-xs">⚠ sin número</span>
                    )}
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell text-neutral-600 align-middle truncate" title={c.address || ""}>{c.address || "—"}</td>
                  <td className="py-3 px-4 hidden md:table-cell text-neutral-600 align-middle truncate">{c.phone || "—"}</td>
                  <td className="py-3 px-4 text-right align-middle whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(c)}
                        className="p-1 rounded hover:bg-orange-100 text-orange-700"
                        title="Editar"
                        data-testid={`rc-edit-${c.id}`}
                      ><Edit3 className="h-3.5 w-3.5" /></button>
                      <button
                        type="button"
                        onClick={() => handleDelete(c)}
                        className="p-1 rounded hover:bg-red-50 text-red-600"
                        title="Eliminar"
                        data-testid={`rc-delete-${c.id}`}
                      ><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Suppress unused-import lint warnings if any:
void X;
